import Icon from './Icon.jsx';
import { MiniToggle } from './Switch.jsx';

// The individual senders behind a category — the answer to "which digests am I
// actually on?" and "can I mute just THAT alert?". Rendered inside a category row
// in NotificationPrefs when it's expanded, and as a per-event card at the bottom.
//
// Mobile-first: one row per item, name and context stacked in a single column
// that wraps, the switch pinned right with a 44px tap target. Nothing sits side
// by side, so a long alert name on a phone wraps instead of squeezing the switch.

// A line of context under the name — the event it belongs to, then whatever the
// category thinks is worth knowing (cadence, channels, ticket status…).
function contextLine(it) {
  return [it.eventName, it.meta].filter(Boolean).join(' · ');
}

export function SourceList({ items, group, busy, onToggle, compact }) {
  if (!items.length) {
    return (
      <div style={{ padding: compact ? '4px 10px 10px 40px' : '4px 12px 12px 42px', fontSize: 12, color: 'var(--muted)' }}>
        {group?.empty || 'Nothing here yet.'}
      </div>
    );
  }
  return (
    <div style={{ padding: compact ? '0 10px 8px' : '0 12px 10px' }}>
      {items.map((it) => {
        const off = !it.on || it.mutedByEvent;
        return (
          <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: compact ? 26 : 28, minHeight: 44, borderTop: '1px solid var(--hairline)' }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, opacity: off ? 0.55 : 1, padding: '6px 0' }}>
              {it.name}
              {it.paused && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 999, padding: '1px 6px' }}>Paused</span>}
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400, marginTop: 1 }}>
                {/* An event switch overrides the item's own — say so, rather than
                    showing an "on" toggle for something that isn't being sent. */}
                {it.mutedByEvent ? `Muted with ${it.eventName || 'this event'}` : contextLine(it)}
              </div>
            </span>
            {/* Dimmed when something OTHER than this switch is stopping it — a
                muted event, or a paused/finished sender. The switch keeps its
                own value; it just shouldn't read as actively notifying. */}
            <MiniToggle on={it.on} dimmed={it.mutedByEvent || it.paused} busy={busy === it.key} size="sm"
              title={it.on ? 'Notifying you — tap to mute' : 'Muted — tap to switch back on'}
              onClick={() => onToggle(it.key, !it.on)} />
          </div>
        );
      })}
    </div>
  );
}

// One switch per event: silences every alert, digest and goal check-in attached
// to it. For the client who runs one festival at a time and wants last month's
// to go quiet without unpicking a dozen individual watchers.
export function EventMutes({ events, busy, onToggle, compact, cardStyle }) {
  if (!events.length) return null;
  const countLine = (e) => {
    const n = { digest: 'digest', alerts: 'alert', goals: 'goal check-in', messages: 'conversation', reports: 'report' };
    return Object.entries(e.counts || {})
      .map(([k, v]) => `${v} ${n[k] || k}${v === 1 ? '' : 's'}`)
      .join(' · ');
  };
  return (
    <div style={{ marginTop: compact ? 8 : 16, ...(compact ? {} : cardStyle) }}>
      <div style={{ padding: compact ? '6px 10px 4px' : '12px 12px 6px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)' }}>
        By event
      </div>
      {events.map((e) => (
        <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: compact ? '5px 10px' : '5px 12px', minHeight: 44, borderBottom: compact ? 'none' : '1px solid var(--hairline)' }}>
          <span style={{ width: 20, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}><Icon name="ticket" size={15} /></span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, opacity: e.on ? 1 : 0.55 }}>
            {e.name}
            <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400, marginTop: 1 }}>
              {e.on ? countLine(e) : 'Everything from this event is muted'}
            </div>
          </span>
          <MiniToggle on={e.on} busy={busy === e.key}
            title={e.on ? 'Tap to mute everything from this event' : 'Tap to switch this event back on'}
            onClick={() => onToggle(e.key, !e.on)} />
        </div>
      ))}
    </div>
  );
}
